import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  Fragment,
} from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { EyeIcon as EyeIconSolid } from '@heroicons/react/20/solid';
import {
  ENRICHED_FUTUREFAIR_BOOTHS,
  enrichBoothsWithDbShows,
  extractBoothCodeFromTitle,
} from '../util/futurefair-data';
import { getFutureFairShows } from '../util/util';
import { useUserShowsStore } from '../stores';
import { useShowActions } from '../hooks';
import { useRouter } from '../util/router';
import { logShowDetailsClickEvent } from '../util/events';
import { useAuth } from '../util/auth';
import { useMedia } from 'react-use';

const FLOOR_MAP_IMAGE = `${process.env.PUBLIC_URL || ''}/ff26_dark2.png`;

const MAP_ZOOM_MAX = 8;
const MAP_ZOOM_LEVELS_FULL = [
  1, 1.25, 1.5, 2, 2.5, 3, 4, 4.25, 4.5, 4.75, 5, 5.25, 5.5, 5.75, 6, 6.5, 7, 7.5, 8,
];

function initialMapZoomScale() {
  if (typeof window === 'undefined') return 4;
  return window.matchMedia('(min-width: 768px)').matches ? 1 : 4;
}

function FloorMap() {
  const auth = useAuth();
  const { push } = useRouter();
  const isDesktopMapZoom = useMedia('(min-width: 768px)');
  const mapZoomMin = isDesktopMapZoom ? 1 : 4;
  const mapZoomLevels = useMemo(
    () => MAP_ZOOM_LEVELS_FULL.filter(z => z >= mapZoomMin - 1e-6 && z <= MAP_ZOOM_MAX + 1e-6),
    [mapZoomMin]
  );

  const [selectedBooth, setSelectedBooth] = useState(null);
  const [viewMode, setViewMode] = useState('map');
  const [futureFairShows, setFutureFairShows] = useState([]);
  const [showsLoading, setShowsLoading] = useState(true);
  const [scale, setScale] = useState(initialMapZoomScale);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const [isDragging, setIsDragging] = useState(false);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const floorMapImgRef = useRef(null);
  const [mapStripMinHeightPx, setMapStripMinHeightPx] = useState(null);
  const [intrinsicMapHeightPx, setIntrinsicMapHeightPx] = useState(null);
  const pinchStartDistRef = useRef(0);
  const pinchStartScaleRef = useRef(1);
  const lastPinchMidXRef = useRef(null);
  const lastPinchMidYRef = useRef(null);
  const seenShowIds = useUserShowsStore(state => state.seenShows);
  const { handleSeen, modals } = useShowActions();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const shows = await getFutureFairShows();
        if (!cancelled) setFutureFairShows(shows);
      } catch (e) {
        console.error('Failed to load Future Fair shows', e);
      } finally {
        if (!cancelled) setShowsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedBooths = useMemo(() => {
    const base =
      futureFairShows.length > 0
        ? enrichBoothsWithDbShows(ENRICHED_FUTUREFAIR_BOOTHS, futureFairShows)
        : ENRICHED_FUTUREFAIR_BOOTHS;
    return [...base].sort((a, b) => a.code.localeCompare(b.code));
  }, [futureFairShows]);

  const showIdToBoothId = useMemo(() => {
    const map = new Map();
    for (const show of futureFairShows) {
      const code = extractBoothCodeFromTitle(show.show_title);
      if (!code) continue;
      const booth = sortedBooths.find(
        b => b.code.trim().toUpperCase() === code.trim().toUpperCase()
      );
      if (booth) map.set(show.show_id, booth.id);
    }
    return map;
  }, [futureFairShows, sortedBooths]);

  const boothIdToShowId = useMemo(() => {
    const map = new Map();
    for (const [showId, boothId] of showIdToBoothId) {
      map.set(boothId, showId);
    }
    return map;
  }, [showIdToBoothId]);

  const getNextZoomLevel = useCallback(
    currentScale => {
      const next = mapZoomLevels.find(z => z > currentScale + 1e-6);
      return next != null ? Math.min(next, MAP_ZOOM_MAX) : MAP_ZOOM_MAX;
    },
    [mapZoomLevels]
  );

  const getPrevZoomLevel = useCallback(
    currentScale => {
      const candidates = mapZoomLevels.filter(
        z => z < currentScale - 1e-6 && z >= mapZoomMin - 1e-6
      );
      if (candidates.length === 0) return mapZoomMin;
      return candidates[candidates.length - 1];
    },
    [mapZoomLevels, mapZoomMin]
  );

  const getDistance = (touch1, touch2) => {
    return Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
  };

  const clampPanValues = useCallback((x, y) => {
    const c = containerRef.current;
    const m = mapRef.current;
    if (!c || !m) return { x: 0, y: 0 };
    const vw = c.clientWidth;
    const vh = c.clientHeight;
    const s = scaleRef.current;
    const cw = m.offsetWidth * s;
    const ch = m.offsetHeight * s;
    if (vw <= 0 || vh <= 0) return { x: 0, y: 0 };
    const minX = Math.min(0, vw - cw);
    const minY = Math.min(0, vh - ch);
    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y)),
    };
  }, []);

  const syncIntrinsicMapHeight = useCallback(() => {
    const wrap = mapRef.current;
    const img = floorMapImgRef.current;
    if (!wrap || !img || img.naturalWidth <= 0) return;
    const cw = wrap.offsetWidth;
    if (cw <= 0) return;
    setIntrinsicMapHeightPx(Math.ceil((img.naturalHeight / img.naturalWidth) * cw));
  }, []);

  useEffect(() => {
    if (viewMode !== 'map') return;
    const desktop =
      typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
    setScale(desktop ? 1 : 4);
    setPan({ x: 0, y: 0 });
  }, [viewMode]);

  useLayoutEffect(() => {
    setScale(s => Math.min(MAP_ZOOM_MAX, Math.max(mapZoomMin, s)));
  }, [mapZoomMin]);

  const handleZoomIn = () => setScale(prev => getNextZoomLevel(prev));
  const handleZoomOut = () => setScale(prev => getPrevZoomLevel(prev));

  const handleResetZoom = () => {
    setScale(mapZoomMin);
    setPan({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (viewMode !== 'map') return undefined;

    let cancelled = false;
    let detach = () => {};

    const rafId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      const container = containerRef.current;
      if (!container) return;

      const useTouchBasedPan = window.matchMedia('(any-pointer: coarse)').matches;

      let wheelAccumulator = 0;

      const handleWheel = e => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          wheelAccumulator += e.deltaY;
          const threshold = 50;
          if (Math.abs(wheelAccumulator) > threshold) {
            if (wheelAccumulator > 0) {
              setScale(prev => getPrevZoomLevel(prev));
            } else {
              setScale(prev => getNextZoomLevel(prev));
            }
            wheelAccumulator = 0;
          }
        }
      };

      const handlePinchTouchMove = e => {
        if (e.touches.length !== 2) return;
        e.preventDefault();
        const d0 = pinchStartDistRef.current;
        if (d0 <= 0) return;
        const D = getDistance(e.touches[0], e.touches[1]);
        const rect = container.getBoundingClientRect();
        const fx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
        const fy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
        const oldS = scaleRef.current;
        const rawS = pinchStartScaleRef.current * (D / d0);
        const newS = Math.min(MAP_ZOOM_MAX, Math.max(mapZoomMin, rawS));
        const ratio = newS / oldS;

        let panDx = 0;
        let panDy = 0;
        if (lastPinchMidXRef.current != null && lastPinchMidYRef.current != null) {
          panDx = fx - lastPinchMidXRef.current;
          panDy = fy - lastPinchMidYRef.current;
        }
        lastPinchMidXRef.current = fx;
        lastPinchMidYRef.current = fy;

        if (Math.abs(ratio - 1) < 1e-7 && Math.abs(panDx) < 0.05 && Math.abs(panDy) < 0.05) {
          return;
        }

        setScale(newS);
        setPan(prev => {
          let x = prev.x;
          let y = prev.y;
          if (Math.abs(ratio - 1) > 1e-7) {
            x = fx - (fx - x) * ratio;
            y = fy - (fy - y) * ratio;
          }
          x += panDx;
          y += panDy;
          return clampPanValues(x, y);
        });
      };

      const handlePinchTouchEnd = () => {
        pinchStartDistRef.current = 0;
        lastPinchMidXRef.current = null;
        lastPinchMidYRef.current = null;
      };

      let touchPan = false;
      let touchLastX = 0;
      let touchLastY = 0;

      const endTouchPan = () => {
        if (!touchPan) return;
        touchPan = false;
        setIsDragging(false);
        document.removeEventListener('touchmove', onDocTouchMove, { capture: true });
        document.removeEventListener('touchend', onDocTouchEnd, { capture: true });
        document.removeEventListener('touchcancel', onDocTouchEnd, { capture: true });
      };

      const onDocTouchMove = e => {
        if (!touchPan || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = t.clientX - touchLastX;
        const dy = t.clientY - touchLastY;
        touchLastX = t.clientX;
        touchLastY = t.clientY;
        e.preventDefault();
        setPan(prev => clampPanValues(prev.x + dx, prev.y + dy));
      };

      const onDocTouchEnd = e => {
        if (e.touches.length === 0) endTouchPan();
        else if (touchPan && e.touches.length === 1) {
          touchLastX = e.touches[0].clientX;
          touchLastY = e.touches[0].clientY;
        }
      };

      const onContainerTouchStart = e => {
        if (e.touches.length === 2) {
          endTouchPan();
          const rect = container.getBoundingClientRect();
          const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
          const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
          lastPinchMidXRef.current = mx;
          lastPinchMidYRef.current = my;
          const d = getDistance(e.touches[0], e.touches[1]);
          pinchStartDistRef.current = Math.max(d, 24);
          pinchStartScaleRef.current = scaleRef.current;
          return;
        }
        if (!useTouchBasedPan || e.touches.length !== 1) return;
        if (e.target instanceof Element && e.target.closest('button')) return;
        endTouchPan();
        touchPan = true;
        setIsDragging(true);
        touchLastX = e.touches[0].clientX;
        touchLastY = e.touches[0].clientY;
        document.addEventListener('touchmove', onDocTouchMove, { passive: false, capture: true });
        document.addEventListener('touchend', onDocTouchEnd, { capture: true });
        document.addEventListener('touchcancel', onDocTouchEnd, { capture: true });
      };

      let panPointerId = null;
      let lastPanX = 0;
      let lastPanY = 0;

      const endPointerPan = () => {
        if (panPointerId == null) return;
        panPointerId = null;
        setIsDragging(false);
        document.removeEventListener('pointermove', onDocPointerMove, { capture: true });
        document.removeEventListener('pointerup', onDocPointerUp, { capture: true });
        document.removeEventListener('pointercancel', onDocPointerUp, { capture: true });
      };

      const onDocPointerMove = e => {
        if (e.pointerId !== panPointerId) return;
        const dx = e.clientX - lastPanX;
        const dy = e.clientY - lastPanY;
        lastPanX = e.clientX;
        lastPanY = e.clientY;
        e.preventDefault();
        setPan(prev => clampPanValues(prev.x + dx, prev.y + dy));
      };

      const onDocPointerUp = e => {
        if (e.pointerId !== panPointerId) return;
        endPointerPan();
      };

      const onContainerPointerDown = e => {
        if (useTouchBasedPan) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target instanceof Element && e.target.closest('button')) return;
        if (panPointerId != null) return;
        panPointerId = e.pointerId;
        lastPanX = e.clientX;
        lastPanY = e.clientY;
        setIsDragging(true);
        document.addEventListener('pointermove', onDocPointerMove, { passive: false, capture: true });
        document.addEventListener('pointerup', onDocPointerUp, { capture: true });
        document.addEventListener('pointercancel', onDocPointerUp, { capture: true });
      };

      container.addEventListener('touchstart', onContainerTouchStart, { passive: true });
      container.addEventListener('touchmove', handlePinchTouchMove, { passive: false });
      container.addEventListener('touchend', handlePinchTouchEnd, { passive: true });
      container.addEventListener('touchcancel', handlePinchTouchEnd, { passive: true });
      container.addEventListener('wheel', handleWheel, { passive: false });
      if (!useTouchBasedPan) {
        container.addEventListener('pointerdown', onContainerPointerDown, { passive: true });
      }

      detach = () => {
        endTouchPan();
        endPointerPan();
        container.removeEventListener('touchstart', onContainerTouchStart);
        container.removeEventListener('touchmove', handlePinchTouchMove);
        container.removeEventListener('touchend', handlePinchTouchEnd);
        container.removeEventListener('touchcancel', handlePinchTouchEnd);
        container.removeEventListener('wheel', handleWheel);
        if (!useTouchBasedPan) {
          container.removeEventListener('pointerdown', onContainerPointerDown);
        }
      };
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      detach();
    };
  }, [viewMode, getNextZoomLevel, getPrevZoomLevel, clampPanValues, mapZoomMin]);

  useLayoutEffect(() => {
    if (viewMode !== 'map') {
      setMapStripMinHeightPx(null);
      setIntrinsicMapHeightPx(null);
      return undefined;
    }
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const sync = () => {
      const h = el.clientHeight;
      if (h > 0) setMapStripMinHeightPx(h);
    };
    sync();

    const ro = new ResizeObserver(() => {
      sync();
      syncIntrinsicMapHeight();
      setPan(p => {
        const n = clampPanValues(p.x, p.y);
        return n.x === p.x && n.y === p.y ? p : n;
      });
    });
    ro.observe(el);
    const mapEl = mapRef.current;
    if (mapEl) ro.observe(mapEl);
    return () => ro.disconnect();
  }, [viewMode, clampPanValues, syncIntrinsicMapHeight]);

  useLayoutEffect(() => {
    if (viewMode !== 'map') return;
    syncIntrinsicMapHeight();
    setPan(p => {
      const n = clampPanValues(p.x, p.y);
      return n.x === p.x && n.y === p.y ? p : n;
    });
  }, [viewMode, scale, mapStripMinHeightPx, clampPanValues, syncIntrinsicMapHeight]);

  const mapLayerMinHeightPx = useMemo(
    () => intrinsicMapHeightPx ?? null,
    [intrinsicMapHeightPx]
  );

  const boothsWithLinkedShow = useMemo(
    () => sortedBooths.filter(b => boothIdToShowId.has(b.id)),
    [sortedBooths, boothIdToShowId]
  );

  const seenCount = useMemo(
    () =>
      boothsWithLinkedShow.filter(b => {
        const sid = boothIdToShowId.get(b.id);
        return sid != null && seenShowIds.has(sid);
      }).length,
    [boothsWithLinkedShow, boothIdToShowId, seenShowIds]
  );

  const remainingCount = Math.max(0, boothsWithLinkedShow.length - seenCount);

  const isBoothSeen = useCallback(
    boothId => {
      const showId = boothIdToShowId.get(boothId);
      return showId != null && seenShowIds.has(showId);
    },
    [boothIdToShowId, seenShowIds]
  );

  const handleToggleSeenForShow = useCallback(
    showId => {
      if (!showId) return;
      return handleSeen(showId);
    },
    [handleSeen]
  );

  const getBoothOverlayClassName = () =>
    'pointer-events-auto absolute rounded-sm border-0 transition-all duration-200 focus:outline-none hover:bg-white/10';

  const openShowDetails = (showId, source) => {
    void logShowDetailsClickEvent(auth.user?.id ?? null, String(showId), source).catch(() => {});
    push(`/show/${showId}`);
  };

  const selectedShowId = selectedBooth ? boothIdToShowId.get(selectedBooth.id) : null;

  return (
    <div
      id="floor-map-root"
      className="flex h-full min-h-0 w-full max-w-[100vw] flex-1 flex-col overflow-hidden bg-white font-oswald md:px-8"
    >
      <header className="flex shrink-0 items-start justify-between gap-4 px-5 py-3 sm:px-6 md:px-0 md:py-3">
        <div className="min-w-0">
          <h1 className="text-base font-bold leading-tight text-gray-900 sm:text-xl md:text-2xl">
            Future Fair 2026
          </h1>
          <p className="mt-0.5 text-[11px] text-gray-500 sm:text-sm">535 W 28th St</p>
          {showsLoading && (
            <p className="mt-1 text-[11px] text-gray-400 md:text-xs">Syncing artwork…</p>
          )}
        </div>
        <div className="flex shrink-0 items-start gap-5 sm:gap-8 md:gap-10">
          <div className="flex flex-col items-end text-right">
            <span className="text-lg font-bold tabular-nums leading-none text-gray-900 sm:text-xl md:text-2xl">
              {seenCount}
            </span>
            <span className="mt-0.5 text-[10px] text-gray-600 sm:text-xs">Seen</span>
          </div>
          <div className="flex flex-col items-end text-right">
            <span className="text-lg font-bold tabular-nums leading-none text-gray-900 sm:text-xl md:text-2xl">
              {remainingCount}
            </span>
            <span className="mt-0.5 text-[10px] text-gray-600 sm:text-xs">remaining</span>
          </div>
        </div>
      </header>

      <div id="floor-map-main" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {viewMode === 'map' ? (
          <>
            <div className="hidden shrink-0 items-center gap-2 px-5 py-2 md:flex md:px-0">
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={scale <= mapZoomMin + 1e-6}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 font-bold text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
              >
                −
              </button>
              <span className="min-w-[3.25rem] text-center text-sm text-gray-600 tabular-nums">
                {Math.round(scale * 100)}%
              </span>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={scale >= MAP_ZOOM_MAX - 1e-6}
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 font-bold text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
              >
                +
              </button>
              {(Math.abs(scale - mapZoomMin) > 1e-6 ||
                Math.abs(pan.x) > 0.5 ||
                Math.abs(pan.y) > 0.5) && (
                <button
                  type="button"
                  onClick={handleResetZoom}
                  className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  Reset
                </button>
              )}
            </div>

            <div
              id="floor-map-scroll"
              ref={containerRef}
              className={`relative h-full min-h-0 flex-1 overflow-hidden bg-gray-100 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{
                touchAction: 'none',
                overscrollBehavior: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              <div
                ref={mapRef}
                className="relative isolate block w-full min-w-full shrink-0 overflow-visible bg-gray-100 will-change-transform"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px)`,
                }}
              >
                <div
                  className="relative isolate w-full min-w-full overflow-visible bg-gray-100"
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: '0 0',
                    ...(mapLayerMinHeightPx != null && mapLayerMinHeightPx > 0
                      ? { minHeight: mapLayerMinHeightPx }
                      : {}),
                  }}
                >
                <div className="relative z-[1] min-w-full">
                  <div className="relative w-full shrink-0">
                    <img
                      ref={floorMapImgRef}
                      src={FLOOR_MAP_IMAGE}
                      alt="Future Fair floor map"
                      className="relative z-[1] block h-auto w-full max-w-none select-none pointer-events-none"
                      draggable={false}
                      onLoad={() => {
                        syncIntrinsicMapHeight();
                        setPan(p => {
                          const n = clampPanValues(p.x, p.y);
                          return n.x === p.x && n.y === p.y ? p : n;
                        });
                      }}
                    />
                    <div className="pointer-events-none absolute inset-0 z-10">
                      {sortedBooths.map(booth => {
                        const seen = isBoothSeen(booth.id);
                        const isSelected = selectedBooth?.id === booth.id;
                        return (
                          <button
                            key={booth.id}
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedBooth(booth);
                            }}
                            className={getBoothOverlayClassName()}
                            style={{
                              left: `${booth.x}%`,
                              top: `${booth.y}%`,
                              width: `${booth.width}%`,
                              height: `${booth.height}%`,
                              backgroundColor: isSelected
                                ? 'rgba(34, 197, 94, 0.5)'
                                : seen
                                  ? 'rgba(59, 130, 246, 0.5)'
                                  : undefined,
                            }}
                            title={booth.name || booth.code}
                          >
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div
                  className="pointer-events-none absolute inset-0 z-[20] shadow-[inset_0_0_100px_rgba(0,0,0,0.08)]"
                  aria-hidden
                />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto bg-white pb-24 md:pb-20">
          {futureFairShows.length === 0 ? (
            <p className="text-sm text-gray-500 italic text-center py-12 px-4">
              No Future Fair shows found in the database yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {futureFairShows.map(show => {
                const boothId = showIdToBoothId.get(show.show_id);
                const seen = seenShowIds.has(show.show_id);
                const thumb = Array.isArray(show.image_urls)
                  ? show.image_urls[0]
                  : typeof show.image_urls === 'string'
                    ? tryParseJsonArray(show.image_urls)[0]
                    : null;

                return (
                  <li key={show.show_id} className="flex gap-3 p-4 hover:bg-gray-50 items-center">
                    <button
                      type="button"
                      onClick={() => openShowDetails(show.show_id, 'future_fair_list_thumb')}
                      className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 hover:opacity-90"
                      aria-label="Open show details"
                    >
                      {thumb ? (
                        <img src={thumb} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                          FF
                        </div>
                      )}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm leading-snug">{show.show_title}</p>
                      {show.venues?.venue_name && (
                        <p className="text-xs text-gray-500 mt-0.5">{show.venues.venue_name}</p>
                      )}
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => openShowDetails(show.show_id, 'future_fair_list')}
                          className="inline-flex rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
                        >
                          Show details
                        </button>
                      </div>
                    </div>
                    {boothId && (
                      <button
                        type="button"
                        onClick={() => void handleToggleSeenForShow(show.show_id)}
                        className="shrink-0 rounded-full p-1 text-gray-900 hover:bg-gray-100 border-0 bg-transparent"
                        aria-label={seen ? 'Mark as not seen' : 'Mark as seen'}
                      >
                        {seen ? (
                          <EyeIconSolid className="h-8 w-8 text-black" />
                        ) : (
                          <EyeIcon className="h-8 w-8" />
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        )}
      </div>

      <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-40 flex rounded-full border border-gray-200 bg-white/95 p-1 shadow-lg backdrop-blur-sm md:bottom-6">
        <button
          type="button"
          onClick={() => setViewMode('map')}
          className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
            viewMode === 'map' ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          Map
        </button>
        <button
          type="button"
          onClick={() => setViewMode('list')}
          className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
            viewMode === 'list' ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          List
        </button>
      </div>

      <Transition appear show={Boolean(selectedBooth)} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setSelectedBooth(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end sm:items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform rounded-t-2xl sm:rounded-2xl bg-white p-5 shadow-xl border border-gray-200">
                  {selectedBooth && (
                    <>
                      <div className="flex justify-between gap-3 items-start mb-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                              {selectedBooth.code}
                            </span>
                            {selectedBooth.location && (
                              <span className="text-xs text-gray-500">{selectedBooth.location}</span>
                            )}
                          </div>
                          <Dialog.Title className="text-lg font-bold text-gray-900 mt-1">
                            {selectedBooth.name}
                          </Dialog.Title>
                          {selectedBooth.artists?.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                              {selectedBooth.artists.join(', ')}
                            </p>
                          )}
                          {selectedBooth.medium && (
                            <p className="text-xs text-gray-400 mt-1 italic">{selectedBooth.medium}</p>
                          )}
                          {selectedBooth.short_description && (
                            <p className="text-sm text-gray-700 mt-2">{selectedBooth.short_description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {selectedShowId && (
                            <button
                              type="button"
                              onClick={() => void handleToggleSeenForShow(selectedShowId)}
                              className="cursor-pointer rounded-full p-1 text-gray-900 hover:bg-gray-100 border-0 bg-transparent"
                              aria-label={
                                isBoothSeen(selectedBooth.id) ? 'Mark as not seen' : 'Mark as seen'
                              }
                            >
                              {isBoothSeen(selectedBooth.id) ? (
                                <EyeIconSolid className="w-8 h-8" />
                              ) : (
                                <EyeIcon className="w-8 h-8" />
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setSelectedBooth(null)}
                            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Close"
                          >
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                      </div>

                      {(selectedBooth.url || selectedBooth.instagram) && (
                        <div
                          className={`flex flex-wrap gap-x-4 gap-y-1 text-sm ${
                            selectedShowId ? 'mb-3' : 'mb-4'
                          }`}
                        >
                          {selectedBooth.url && (
                            <a
                              href={selectedBooth.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              Website ↗
                            </a>
                          )}
                          {selectedBooth.instagram && (
                            <a
                              href={selectedBooth.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pink-600 hover:underline"
                            >
                              Instagram ↗
                            </a>
                          )}
                        </div>
                      )}

                      {selectedShowId && (
                        <button
                          type="button"
                          onClick={() => {
                            openShowDetails(selectedShowId, 'future_fair_modal');
                            setSelectedBooth(null);
                          }}
                          className="mb-4 inline-flex rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                        >
                          Open show details
                        </button>
                      )}

                      {selectedBooth.artworks?.length > 0 ? (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">Artwork</h3>
                          <div className="flex gap-3 overflow-x-auto pb-2">
                            {selectedBooth.artworks.map((artwork, index) => (
                              <button
                                key={`${artwork.image}-${index}`}
                                type="button"
                                disabled={!selectedShowId}
                                onClick={() => {
                                  if (selectedShowId) {
                                    openShowDetails(selectedShowId, 'future_fair_artwork');
                                    setSelectedBooth(null);
                                  }
                                }}
                                className="flex-shrink-0 w-36 text-left disabled:opacity-50"
                              >
                                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                                  {artwork.image ? (
                                    <img
                                      src={artwork.image}
                                      alt={artwork.title}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : null}
                                </div>
                                <p className="text-xs font-medium text-gray-900 mt-1 truncate">
                                  {artwork.title}
                                </p>
                                <p className="text-[11px] text-gray-600 truncate">{artwork.artist}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No artwork images for this booth yet.</p>
                      )}
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      {modals}
    </div>
  );
}

function tryParseJsonArray(s) {
  try {
    const p = JSON.parse(s);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

export default FloorMap;
